import type { IFuelEfficiencyTrendPoint } from '../_types'

/** 공인연비 대비 실연비 비율 (%) */
export const calcOfficialRatio = (
  realFuelEconomy: number,
  officialFuelEconomy: number
) => {
  if (officialFuelEconomy <= 0) return 0
  return Math.round((realFuelEconomy / officialFuelEconomy) * 100)
}

/** 실연비 게이지 상태 단계 */
export type TRealFuelStatusLevel = 'great' | 'good' | 'low'

/** 실연비 게이지 상태 (공인연비 대비 비율 기준) */
export interface IRealFuelStatus {
  /** 상태 단계 (아이콘 매핑용) */
  level: TRealFuelStatusLevel
  /** 게이지 상단 버블 라벨 (예: '양호해요') */
  label: string
  /** 노트 문장 중간 표현 (예: '공인연비와 비슷하게') */
  phrase: (officialLabel: string) => string
  /** 게이지 아크·실연비 수치·공인연비 대비 색상 (Figma 상태별 컬러) */
  color: string
}

/** 상태별 컬러 — Figma 실연비 게이지 (훌륭 초록 / 양호 파랑 / 저조 빨강) */
export const REAL_FUEL_STATUS_COLOR = {
  great: '#2BB774',
  good: '#00AFFF',
  low: '#F84848',
} as const

/**
 * 공인연비 대비 비율 → 상태(단계/라벨/문구/색상) 매핑.
 * 임계값(Figma 안내 기준): 80% 미만 저조 / 80~100% 양호 / 100% 이상 훌륭.
 */
export const getRealFuelStatus = (ratio: number): IRealFuelStatus => {
  if (ratio >= 100) {
    return {
      level: 'great',
      label: '훌륭해요',
      phrase: officialLabel => `${officialLabel}보다 높게`,
      color: REAL_FUEL_STATUS_COLOR.great,
    }
  }
  if (ratio >= 80) {
    return {
      level: 'good',
      label: '양호해요',
      phrase: officialLabel => `${officialLabel}와 비슷하게`,
      color: REAL_FUEL_STATUS_COLOR.good,
    }
  }
  return {
    level: 'low',
    label: '저조해요',
    phrase: officialLabel => `${officialLabel}보다 낮게`,
    color: REAL_FUEL_STATUS_COLOR.low,
  }
}

/** 실연비 잠금 이벤트 status — 게이지 등급 (공인연비 없으면 no_official) */
export type TLockMileageStatus = 'great' | 'good' | 'poor' | 'no_official'

/** 게이지 등급 판정 결과를 잠금 이벤트 status 값으로 변환한다 (화면 등급과 같은 함수 사용) */
export const getLockMileageStatus = (
  realFuelEconomy: number,
  officialFuelEconomy: number
): TLockMileageStatus => {
  if (officialFuelEconomy <= 0) return 'no_official'
  const { level } = getRealFuelStatus(
    calcOfficialRatio(realFuelEconomy, officialFuelEconomy)
  )
  return level === 'low' ? 'poor' : level
}

/** 추이 잠금 방향 — 문구와 이벤트 status에 같은 값을 쓴다 */
export type TTrendDirection = 'insufficient' | 'down' | 'up' | 'flat'

/** 추이 방향 판정에 필요한 최소 월별 데이터 수 (0~1개월이면 데이터 부족) */
const TREND_DIRECTION_MIN_MONTHS = 2
/** 연속 판정 구간 — M-3 ~ M0 (4개월) */
const TREND_DIRECTION_WINDOW = 4
const MONTHS_IN_YEAR = 12

/** 그래프 표시값 기준(소수 첫째 자리) 비교값 */
const toTrendCompareValue = (value: number) => Math.round(value * 10) / 10

/** '7월' → 7. 파싱 실패 시 null */
const parseTrendMonth = (month: string): number | null => {
  const parsed = Number.parseInt(month, 10)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= MONTHS_IN_YEAR
    ? parsed
    : null
}

/** 구간의 월이 빠짐없이 이어지는지 (12월→1월 포함) */
const isConsecutiveMonths = (points: IFuelEfficiencyTrendPoint[]) =>
  points.every((point, index) => {
    if (index === 0) return true
    const prev = parseTrendMonth(points[index - 1].month)
    const current = parseTrendMonth(point.month)
    if (prev === null || current === null) return false
    return current === (prev % MONTHS_IN_YEAR) + 1
  })

/**
 * 최근 6개월 추이 방향 판정 (Figma 추이 잠금 문구 케이스 10250:5389~5689).
 * 판정 순서: 데이터 부족 → 3개월 연속 하락 → 3개월 연속 상승 → 유지·변동
 * - 데이터 부족: 실제 월별 값이 0~1개월
 * - 하락: M-3 ≥ M-2 ≥ M-1 ≥ M0 이고 M-3 > M0 / 상승: 반대 (M0 = 그래프 '최근' 막대)
 * - 4개월이 안 되거나 최근 4개월 중 빠진 달이 있으면 유지·변동
 * 비교는 그래프 표시값 기준(소수 첫째 자리 반올림)으로 한다.
 */
export const getTrendDirection = (
  trend: IFuelEfficiencyTrendPoint[],
  monthCount: number
): TTrendDirection => {
  if (monthCount < TREND_DIRECTION_MIN_MONTHS) return 'insufficient'
  if (trend.length < TREND_DIRECTION_WINDOW) return 'flat'

  const window = trend.slice(-TREND_DIRECTION_WINDOW)
  if (!isConsecutiveMonths(window)) return 'flat'

  const values = window.map(point => toTrendCompareValue(point.value))
  const first = values[0]
  const last = values[values.length - 1]
  const pairs = values.slice(1).map((value, index) => [values[index], value])

  if (pairs.every(([prev, next]) => prev >= next) && first > last) return 'down'
  if (pairs.every(([prev, next]) => prev <= next) && first < last) return 'up'
  return 'flat'
}

/**
 * 추이 영역(제목·차트·평균·잠금) 노출 여부 — 실제 월별 데이터 개수 기준.
 * - A: 2개월 이상일 때만 노출 (1개월 이하면 추세로 보여줄 의미가 없음)
 * - B(잠금): 1개월이라도 있으면 잠금 노출 (1개월은 '데이터 부족' 문구), 0개월이면 미노출
 */
export const shouldShowTrendSection = ({
  monthCount,
  isLock,
}: {
  monthCount: number
  isLock: boolean
}): boolean =>
  isLock ? monthCount >= 1 : monthCount >= TREND_DIRECTION_MIN_MONTHS

/** 추이 잠금 문구 2줄 (Figma 추이 잠금 문구 케이스). realLabel = 실연비 / 실전비 */
const TREND_LOCK_MESSAGE: Record<
  TTrendDirection,
  (realLabel: string) => [string, string]
> = {
  insufficient: realLabel => [
    `최근 ${realLabel}가 쌓이는 중이에요`,
    '앱에서 먼저 확인해보세요',
  ],
  down: realLabel => [
    '이 차, 최근 3개월 연속',
    `${realLabel}가 떨어지고 있어요`,
  ],
  up: realLabel => ['이 차, 최근 3개월 연속', `${realLabel}가 오르고 있어요`],
  flat: realLabel => ['이 차의 최근 6개월', `${realLabel} 흐름을 확인해보세요`],
}

export const getTrendLockMessage = (
  direction: TTrendDirection,
  realLabel: string
): [string, string] => TREND_LOCK_MESSAGE[direction](realLabel)

/** AI 실사용 분석 문구 조각 — blur가 true면 잠금 화면(B)에서 흐림 처리한다 */
export interface IAiSummarySegment {
  text: string
  blur: boolean
}

/**
 * 잠금 화면(B)에서 흐릴 AI 실사용 분석 수치 — 실연비(실전비) 값과 공인연비 대비 비율.
 * buildAiSummary 템플릿 기준:
 * - '…의 실연비는 {11.5~12.1 km/L}이다.' (범위 또는 단일값 + 단위)
 * - '공인연비 12.2km/L 대비 {96%} 수준으로…' (공인연비 값 자체는 화면에 공개돼 있어 흐리지 않는다)
 */
const AI_SUMMARY_LOCK_PATTERN =
  /(?<=실[연전]비는 )[\d.~]+ ?km\/(?:L|kWh)|(?<=대비 )\d+%/g

/** AI 실사용 분석 문구를 흐림 대상 수치 기준으로 나눈다 (매칭 없으면 문구 전체 1조각) */
export const splitAiSummaryForLock = (summary: string): IAiSummarySegment[] => {
  const segments: IAiSummarySegment[] = []
  let cursor = 0
  for (const match of summary.matchAll(AI_SUMMARY_LOCK_PATTERN)) {
    const start = match.index ?? 0
    if (start > cursor) {
      segments.push({ text: summary.slice(cursor, start), blur: false })
    }
    segments.push({ text: match[0], blur: true })
    cursor = start + match[0].length
  }
  if (cursor < summary.length) {
    segments.push({ text: summary.slice(cursor), blur: false })
  }
  return segments
}

/** 오너 리뷰 잠금 status — 리뷰 수 구분 */
export type TLockReviewStatus = 'over3' | 'upto3' | 'none'

export const getLockReviewStatus = (reviewCount: number): TLockReviewStatus => {
  if (reviewCount <= 0) return 'none'
  return reviewCount > 3 ? 'over3' : 'upto3'
}
